<template>
  <Layout>
    <div class="mc-fleet-task">
      <PageHeader :title="title" :sub-title="subTitle">
        <template #afterTitle>
          <div class="d-flex gap-2">
            <McListDownloader
              :pageOptions="downloadPageOptions"
              :fileFormatOptions="['excel']"
              @download="handleDownload"
            >
              <template #after-content="{ download }">
                <button
                  v-for="(provider, key) in providers"
                  type="button"
                  class="download-btn"
                  @click="download(provider.type, provider.code)"
                  :key="key"
                >
                  <span><img :src="provider.icon" :alt="provider.type" /></span>
                  {{ $t('global.downloadFor') }} {{ provider.label }}
                </button>
              </template>
            </McListDownloader>
            <b-dropdown
              id="dropdown-right"
              right
              text="Right align"
              variant="primary"
              class="mc-dropdown"
            >
              <template #button-content>
                <div class="d-flex align-items-center">
                  <span>{{ $t('global.addField') }}</span>
                  <i class="ms-1 mc-icon icon-outline_chevron-down"></i>
                </div>
              </template>
              <b-dropdown-item
                v-for="item in dropdownItems"
                :key="item.label"
                @click="$router.push({ name: item.routeName })"
              >
                {{ $t(item.label) }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </PageHeader>
      <McTable
        withCard
        searchable
        stickyHeader
        bulkDeleteApi
        hasEmit:deleteError
        hasCheckbox
        hasDelete
        :hasSelectAll="false"
        :fields="computedFields"
        :apiClient="apiClient"
        :activeFilters="activeFilters"
        :customHandlers="customHandlers"
        @deleteError="handleDeleteError"
      >
        <template #before-table="{ isSelected, showDeleteModal }">
          <div class="d-flex justify-content-between mb-3">
            <McInlineFilter
              :filterItems="inlineFilterItems"
              :activeKeys="activeFilters['status.name']"
              @update:activeKeys="
                handleChangeActiveFilters([{ key: 'status.name', values: $event }])
              "
            />

            <div class="d-flex gap-2">
              <McTableFilter
                :activeFilters="activeFilters"
                :parseFilterDataMappings="parseFilterDataMappings"
                @submit="handleChangeActiveFilters"
              >
                <template
                  #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }"
                >
                  <InputTableFilter
                    :formValues="formValues"
                    :fullObjects="fullObjects"
                    @update:formValues="updateFormValues($event)"
                    @update:fullObjects="updateFullObjects($event)"
                  />
                </template>
              </McTableFilter>

              <!-- NOTE: Hidden for next enhancement -->
              <ActionsDropdown
                v-if="false"
                :disabled="!isSelected"
                :actionHandlers="{ handleDelete: showDeleteModal }"
              />

              <McButton
                v-if="isSelected"
                @click="showDeleteModal"
                variant="secondary-red"
                :text="$t('global.delete')"
                icon="icon-outline_trash"
              />
            </div>
          </div>
        </template>
        <!-- status -->
        <template #[`cell(status.name)`]="{ item }">
          <template>
            <FieldStatus
              :item="{
                ...item,
                status: { ...item.status, name: $t(idCostStatusMapper[item.status.name]) },
              }"
            />
          </template>
        </template>

        <!-- date -->
        <template #cell(date)="{ value }">
          <template v-if="value">
            <div>{{ formatIndexDate(value).date }}</div>
          </template>
          <template v-else>-</template>
        </template>

        <!-- latest_exported_on -->
        <template #cell(latest_exported_on)="{ value }">
          <template v-if="value">
            <div>{{ formatIndexDate(value).date }}</div>
            <div class="__time">{{ formatIndexDate(value).time }}</div>
          </template>
          <template v-else>-</template>
        </template>

        <!-- created on -->
        <template #cell(created_on)="{ value }">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="__time">{{ formatIndexDate(value).time }}</div>
        </template>

        <!-- reference_type -->
        <template #[`cell(reference_type)`]="{ value }">{{
          $t(idCostTypeMapper[value]) || '-'
        }}</template>

        <!-- fleet_task.number -->
        <template #[`cell(fleet_task.number)`]="{ value }"> {{ value || '-' }} </template>

        <!-- vehicle.license_plate -->
        <template #[`cell(vehicle.license_plate)`]="{ value }"> {{ value || '-' }} </template>

        <!-- driver_user -->
        <template #[`cell(driver_user.name)`]="{ value }"> {{ value || '-' }} </template>
      </McTable>
    </div>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McTable from '@/components/McTable'
import McButton from '@/components/McButton'
import McInlineFilter from '@/components/McInlineFilter'
import McListDownloader from '@/components/McListDownloader'
import { MixPanel } from '@/mixin/mixpanel'
import McTableFilter from '@/components/McTableFilter'
import InputTableFilter from './components/InputTableFilter'
import dropdownItems from './lib/constants/create-dropdown-items'
import * as FleetTask from '@/services/rest-api/lib/cost-management/fleet-task-cost'
import { formatIndexDate } from '@/helpers/tzDate'
import { fields } from './lib/fields/costManagementFields'
import filterMappings from './lib/constants/filter-mappings'
import toastErrors from './lib/constants/toast-errors'
import inlineFilterItems from './lib/constants/inline-filter-items'
import parseFilterDataMappings from './lib/constants/parse-filter-data-mappings'
import FieldStatus from './components/Field/Status.vue'
import omitBy from 'lodash/omitBy'
import { errorMessage } from '@/helpers/errorHandler'
import providers from './lib/constants/providers'
import { idCostTypeMapper, idCostStatusMapper } from './lib/constants/constants-mapper'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
moment.locale('id')
export default {
  page: {
    title: 'Cost Management',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McTable,
    McButton,
    McInlineFilter,
    McListDownloader,
    FieldStatus,
    McTableFilter,
    InputTableFilter,
  },
  mixins: [MixPanel, fields],
  data() {
    return {
      idCostTypeMapper,
      idCostStatusMapper,
      dropdownItems,
      inlineFilterItems,
      activeFilters: {},
      params: {},
      metadata: null,
      providers,
      errorMessageList: {
        'found transaction status other than confirmed':
          'costManagement.list.transactionStatusOtherThanConfirmed',
      },
    }
  },
  computed: {
    title() {
      return this.$t('costManagement.title')
    },
    subTitle() {
      return this.$t('costManagement.subtitle')
    },
    apiClient() {
      let getFleetTask = FleetTask.get(null, this.handleSetParams, this.handleSetMetadata)

      if (Object.keys(this.activeFilters).length > 0) {
        const queryParamFilters = []

        // mapping the activeFilters to be sent as query params
        Object.keys(this.activeFilters).forEach((key) => {
          let filterValue = this.activeFilters[key]

          // add hours directly since backend doesn't support date only filter for created_on variable
          if (key === 'created_on:gte') {
            filterValue = new Date(filterValue)
            filterValue.setHours(0, 0, 0)
            filterValue = filterValue.toISOString()
          } else if (key === 'created_on:lte') {
            filterValue = new Date(filterValue)
            filterValue.setHours(23, 59, 59)
            filterValue = filterValue.toISOString()
          } else if (key === 'latest_exported_on:is') {
            if (!filterValue) return
            key = `latest_exported_on:${filterValue}`
            filterValue = null
          }

          const filter = filterMappings[key]?.(key, filterValue) ?? undefined
          queryParamFilters.push(filter)
        })

        getFleetTask = FleetTask.get(
          queryParamFilters,
          this.handleSetParams,
          this.handleSetMetadata
        )
      }

      return {
        get: getFleetTask,
        delete: FleetTask.deleteMany,
      }
    },
    customHandlers() {
      return {
        handleClickRow: this.handleClickRow,
      }
    },
    downloadPageOptions() {
      return [
        { label: this.$t('global.currentPage'), amount: this.metadata?.count, key: 'current_page' },
        { label: this.$t('global.allPages'), amount: this.metadata?.total_count, key: 'all' },
      ]
    },
    parseFilterDataMappings() {
      return parseFilterDataMappings
    },
    computedFields() {
      let fields = [...this.fields.filter((field) => (field.hide ? !field.hide() : true))]
      return fields
    },
  },
  methods: {
    formatIndexDate,
    handleClickRow(item) {
      if (item.reference_type === 'Biaya Perjalanan') {
        this.$router.push({ name: 'Detail Cost Management', params: { id: item.id } })
      } else if (item.reference_type === 'Biaya Kendaraan') {
        this.$router.push({ name: 'Detail Vehicle Cost Management', params: { id: item.id } })
      } else {
        this.$router.push({ name: 'Detail Other Cost Management', params: { id: item.id } })
      }
    },
    handleDeleteError(errors) {
      let errorMessage = 'Gagal dihapus, Ada status Dokumen Biaya selain Draf.'

      // TODO: Change the condition to error.message == 'invalid status', after the APIClient error return refactored to return both error message and error fields.
      if (errors.status === 400 && errors.errors['ids.0'][0] === 'invalid status') {
        errorMessage = toastErrors['failed-to-bulk-delete']
      }

      this.$mcToast.error(errorMessage)
    },
    handleChangeActiveFilters(filters) {
      /*
        filters is Array of Object
        example:
        [
          {
            key: 'created_on:lte',
            values: '2023-08-29T17:00:00Z'
          }
        ]
        */

      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null or undefined
        if (filter.values === null || filter.values === undefined) return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })
    },
    handleDownload(format, dataAmount, code = null) {
      if (code) {
        this.downlodByProvider(format, dataAmount, code)
        return
      }

      // Remove undefined params
      const params = omitBy(
        {
          ...this.params,
          format,
        },
        (value) => value === undefined
      )

      if (dataAmount === 'all') {
        delete params['limit']
        delete params['page']
      }

      const queries = Helper.methods.queryParamsToString(params)

      window.open(`${process.env.VUE_APP_API_FINANCIAL_RECORD_URL}/transaction?${queries}`, '_self')
      this.track('Download Cost')
    },
    async downlodByProvider(format, dataAmount, code) {
      const params = omitBy(
        {
          ...this.params,
        },
        (value) => value === undefined
      )

      if (dataAmount === 'all') {
        delete params['limit']
        delete params['page']
      }

      const data = await FleetTask.downloadAccurate(params)
      if (data.errors) {
        this.$mcToast.error(errorMessage(this.errorMessageList[data.message]))
        return
      }

      // Convert excel binary response into downloadable format
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      const formattedDate = moment().format('YYYY-MM-DD HH.mm.ss')
      link.setAttribute('download', `Accurate other payment - ${formattedDate}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      const providerText = code.charAt(0).toUpperCase() + code.slice(1).toLowerCase()
      this.track(`Export Cost Management to ${providerText}`)
    },
    handleSetParams(params) {
      this.params = params
    },
    handleSetMetadata(metadata) {
      this.metadata = metadata
    },
  },
}
</script>
<style lang="scss">
.mc-dropdown {
  .dropdown-item:hover,
  .dropdown-item:focus {
    background-color: $mep-primary-400 !important;
    color: $white;
  }

  .mc-icon::before {
    color: white;
  }
}
.__time {
  color: $mep-color-text-200;
}
</style>
